import { apiClient } from "@base/api/client";
import type { PaginationResponse } from "@base/model/types";
import type { Document, DocumentType } from "@base/model/types/document";
import { useMutation, useQuery, useQueryClient, useSuspenseInfiniteQuery } from "@tanstack/react-query";
import type { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { toast } from "sonner";

export interface FetchDocumentsParams {
  projectId: number;
  keyword?: string;
  type?: DocumentType;
  page?: number;
  page_size?: number;
}

export type InfiniteDocumentsParams = Omit<FetchDocumentsParams, "page">;

type DocumentsResponse = PaginationResponse<Document[]>;

export async function fetchDocuments(params: FetchDocumentsParams): Promise<DocumentsResponse> {
  const { projectId, ...queryParams } = params;
  const response = await apiClient.get<DocumentsResponse>(`/project-management/api/projects/${projectId}/documents`, {
    params: queryParams,
  });
  return response.data;
}

export function documentsQueryOptions(params: FetchDocumentsParams) {
  return {
    queryKey: ["documents", params.projectId, params] as const,
    queryFn: () => fetchDocuments(params),
  };
}

export function useDocumentsQuery(
  params: FetchDocumentsParams,
  options?: Omit<UseQueryOptions<DocumentsResponse>, "queryKey" | "queryFn">,
) {
  return useQuery({ ...documentsQueryOptions(params), ...options });
}

interface DeleteDocumentParams {
  projectId: number;
  documentId: number;
  type: DocumentType;
}

async function deleteDocument({ projectId, documentId, type }: DeleteDocumentParams): Promise<void> {
  await apiClient.delete(`/project-management/api/projects/${projectId}/documents`, {
    params: {
      type,
      id: documentId,
    },
  });
}

export function useDeleteDocumentMutation(
  options?: Omit<UseMutationOptions<void, Error, DeleteDocumentParams, unknown>, "mutationFn">,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: deleteDocument,
    onSuccess: (data, variables, onMutateResult, mutationContext) => {
      toast.success("Document deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["documents", "infinite", variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ["documents", variables.projectId] });
      options?.onSuccess?.(data, variables, onMutateResult, mutationContext);
    },
    onError: (error, variables, onMutateResult, mutationContext) => {
      toast.error("Failed to delete document");
      options?.onError?.(error, variables, onMutateResult, mutationContext);
    },
  });
}

export function useInfiniteDocumentsQuery(params: InfiniteDocumentsParams) {
  return useSuspenseInfiniteQuery({
    queryKey: ["documents", "infinite", params.projectId, params] as const,
    queryFn: ({ pageParam }) => fetchDocuments({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.current_page < lastPage.total_pages) {
        return lastPage.current_page + 1;
      }
      return undefined;
    },
  });
}
